import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { Button, Table } from '@/components/ui/daisy';
import { useConfirm } from '@/data/context/ConfirmModalContext';
import { AssessmentItem } from '@/models/Assessment';
import { Breakpoints } from '@/utils/Breakpoint';

import AssessmentCriteriaTableRow from './AssessmentCriteriaTableRow';
import AssessmentItemTypeBadge from './AssessmentItemTypeBadge';

type AssessmentItemTableRowProps = {
  item: AssessmentItem;
  onEdit: () => void;
  onDelete: () => Promise<void>;
};

export default function AssessmentItemTableRow({
  item,
  onEdit,
  onDelete,
}: AssessmentItemTableRowProps) {
  const isXl = useMediaQuery({ minWidth: Breakpoints.xl });
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });
  const confirmDelete = useConfirm();

  const handleDelete = () =>
    confirmDelete({
      onConfirm: onDelete,
      title: t('confirmDeleteAssessmentItem'),
    });

  return (
    <>
      <Table.Row>
        <Table.Cell className="font-semibold text-base-content w-1/3">
          {item.name}
        </Table.Cell>
        <Table.Cell className="min-w-fit text-nowrap">
          <AssessmentItemTypeBadge type={item.type} />
        </Table.Cell>
        <Table.Cell className="font-semibold text-base-content text-center">
          {item.maxGrade}
        </Table.Cell>
        <Table.Cell className="max-w-11 w-20 p-2">
          <Button
            variant="ghost"
            size={isXl ? 'sm' : 'xs'}
            shape="square"
            type="button"
            onClick={onEdit}
            aria-label={t('assessmentItemFormTitle')}
          >
            <PencilSquareIcon className="max-xl:size-3 size-4 text-base-content" />
          </Button>
        </Table.Cell>
        <Table.Cell className="max-w-11 w-20 p-2">
          <Button
            variant="ghost"
            size={isXl ? 'sm' : 'xs'}
            shape="square"
            type="button"
            onClick={handleDelete}
            aria-label={t('deleteAssessmentItem')}
          >
            <TrashIcon className="max-xl:size-3 size-4 text-error" />
          </Button>
        </Table.Cell>
      </Table.Row>
      {item.criteria?.map(criterion => (
        <AssessmentCriteriaTableRow key={criterion.id} criterion={criterion} />
      ))}
    </>
  );
}
